import React, { useEffect, useState } from 'react';
import { openChannel, MSG, DEFAULT_STYLE } from './displayChannel';
import { backgroundCss, textEffectCss } from './themes';
import { slideText } from '../lib/slides';

const FADE_MS = 250;

/**
 * The projector page (/display). Renders whatever the control page broadcasts.
 * All text is rendered as React text nodes (never innerHTML), so lyrics or
 * verse text from third-party sources cannot inject markup.
 */
const DisplayScreen = () => {
  const [state, setState] = useState({ isLive: false, content: { type: 'welcome' }, currentLine: 0, style: DEFAULT_STYLE });
  const [image, setImage] = useState({ imageId: null, url: null });

  useEffect(() => {
    document.title = 'SleekHear Display';
    const channel = openChannel();
    if (!channel) return undefined;
    channel.onmessage = (e) => {
      const data = e.data;
      if (!data) return;
      if (data.type === MSG.STATE && data.state) {
        setState(data.state);
      } else if (data.type === MSG.BACKGROUND && data.blob) {
        const url = URL.createObjectURL(data.blob);
        setImage((prev) => {
          if (prev.url) URL.revokeObjectURL(prev.url);
          return { imageId: data.imageId, url };
        });
      }
    };
    channel.postMessage({ type: MSG.HELLO });
    const onLeave = () => channel.postMessage({ type: MSG.BYE });
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      channel.close();
    };
  }, []);

  // Browsers only allow full screen from a user gesture inside this window.
  const goFullscreen = () => {
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };
  useEffect(() => {
    window.addEventListener('keydown', goFullscreen);
    return () => window.removeEventListener('keydown', goFullscreen);
  }, []);

  const { isLive, content, currentLine, slides, style } = state;
  const s = { ...DEFAULT_STYLE, ...style };
  // Only use the image if it is the one the control page currently selected.
  const imageUrl = s.background && s.background.imageId === image.imageId ? image.url : null;
  const bg = backgroundCss(s.background, s.backgroundColor, imageUrl);

  let key = 'blank';
  let body = null;
  if (isLive && content) {
    if (content.type === 'scripture') {
      const text = slideText(content, slides, currentLine);
      key = `s:${content.reference}:${content.version}:${text}`;
      body = (
        <>
          <div style={{ fontSize: s.fontSize * 0.6, fontWeight: 'bold', marginBottom: 40 }}>
            {content.reference}{content.version ? ` (${content.version})` : ''}
          </div>
          <div style={{ fontSize: s.fontSize, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text}</div>
        </>
      );
    } else if (content.type === 'song') {
      const line = slideText(content, slides, currentLine);
      key = `g:${content.title}:${currentLine}`;
      body = <div style={{ fontSize: s.fontSize * 0.8, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{line}</div>;
    } else {
      key = 'welcome';
      body = (
        <div style={{ textAlign: 'center', color: '#3b82f6' }}>
          <div style={{ fontSize: 80, fontWeight: 'bold', marginBottom: 20 }}>SleekHear</div>
          <div style={{ fontSize: 24, letterSpacing: 4 }}>CHURCH PRESENTATION SYSTEM</div>
        </div>
      );
    }
  }

  return (
    <div
      onClick={goFullscreen}
      data-testid="display-root"
      style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#000000', color: s.fontColor, fontFamily: s.fontFamily, cursor: 'none', overflow: 'hidden',
      }}
    >
      <style>{`@keyframes sleekFade{from{opacity:0}to{opacity:1}}`}</style>
      {/* Off air stays plain black: no background is drawn. */}
      {isLive && (
        <div data-testid="display-background" style={{ position: 'absolute', inset: 0, ...bg.style }} />
      )}
      {isLive && bg.dim > 0 && (
        <div data-testid="display-dim" style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${bg.dim})` }} />
      )}
      {body && (
        <div
          key={key}
          style={{
            position: 'relative', textAlign: 'center', padding: 60, maxWidth: '90%',
            animation: `sleekFade ${FADE_MS}ms ease-out`, ...textEffectCss(s.textEffect),
          }}
        >
          {body}
        </div>
      )}
    </div>
  );
};

export default DisplayScreen;
